import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Send, MessageSquare, TrendingUp, TrendingDown, Save, FileText, Brain, ChevronDown, ChevronUp, X, Pencil } from 'lucide-react';
import { fetchTradesFromDB } from './JournalingService';
import { startJournalingPrompt } from './prompts';
import { trackJournalSessionStarted, trackJournalMessageSent, trackJournalSessionCompleted, trackJournalSummaryViewed } from '../../services/analytics';
import { JournalTradeList } from './journalTradeList';
import { SummaryModal, stripRedundantSections, parseSections, buildTagPills, ScoreBar, TagPill, SectionBlock } from './summaryModal';
import { TradeReplayModal } from './TradeReplayModal';
import { isBuyDirection } from '../../utils/tradeCalculations';
import parse from 'html-react-parser';
import ScreenshotThumbnail from '../../components/ui/ScreenshotThumbnail';
import { getTradeScreenshots } from '../../services/screenshotService';
import { getPlaybooks } from '../../services/playbookService';

const JournalAI = () => {
  // Core state
  const [trades, setTrades] = useState([]);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [messages, setMessages] = useState([]);
  const [tradeScreenshots, setTradeScreenshots] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [journalMode, setJournalMode] = useState(null);
  const [freeJournalText, setFreeJournalText] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [ai_summary, setAi_summary] = useState(null);
  const [playbooks, setPlaybooks] = useState([]);
  const [selectedPlaybookId, setSelectedPlaybookId] = useState(null);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [isRequestingDetails, setIsRequestingDetails] = useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);

  // UI state
  const [activeTab, setActiveTab] = useState('need_journal');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReplayOpen, setIsReplayOpen] = useState(false);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [isSavingSummary, setIsSavingSummary] = useState(false);

  // Refs
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const autosaveTimeoutRef = useRef(null);

  const token = localStorage.getItem('access_token');

  // Constants
  const statusLabels = {
    'need_journal': 'Need Journal',
    'in_progress': 'In Progress',
    'done': 'Done'
  };
  const statusOrder = ['need_journal', 'in_progress', 'done'];

  // Memoized computations
  const groupedTrades = useMemo(() => {
    const groups = { need_journal: [], in_progress: [], done: [] };
    if (!Array.isArray(trades)) return groups;
    
    trades.forEach(trade => {
      const status = trade.journal_status || 'need_journal';
      if (groups[status]) {
        groups[status].push(trade);
      }
    });
    return groups;
  }, [trades]);

  const visibleMessages = useMemo(() => 
    messages.filter(msg => msg.role !== 'system'), 
    [messages]
  );

  // Debounced autosave
  const debouncedAutosave = useCallback((updatedMessages) => {
    const tradeId = selectedTrade?.id || selectedTrade?._id;
    if (!selectedTrade || !tradeId || !Array.isArray(updatedMessages)) {
      return;
    }

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch('/api/v1/journaling/autosave', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            tradeId,
            conversation: updatedMessages
          })
        });
        
        if (!res.ok) {
          console.error('[AUTOSAVE] Error response');
        }
      } catch (err) {
        console.error('[AUTOSAVE] Fetch failed:', err);
      }
    }, 500);
  }, [selectedTrade, token]);

  // Load trades on mount
  useEffect(() => {
    const loadTrades = async () => {
      try {
        const tradesData = await fetchTradesFromDB();
        setTrades(tradesData || []);
      } catch (error) {
        console.error('Error loading trades:', error);
        setTrades([]);
      }
    };
    loadTrades();
  }, []);

  // Scroll to bottom when messages change or loading state changes
  useEffect(() => {
    if (visibleMessages.length > 0 || isLoading) {
      // Use setTimeout to ensure HTML parsing and DOM layout are complete
      const scrollToBottom = () => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      };

      // Scroll immediately
      scrollToBottom();

      // Scroll again after a delay to catch any delayed rendering from HTML parsing
      const timeoutId = setTimeout(scrollToBottom, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [visibleMessages, isLoading]);

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, []);


  // Session management
  const createSessionForInProgress = async (trade) => {
    try {
      const response = await fetch('/api/v1/journaling/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          trade: trade, 
          mode: 'in-progress',
          existingConversation: messages 
        })
      });
      const data = await response.json();
      setSessionId(data.sessionId);
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const startGuidedJournalingSession = async (mode) => {  
    setIsStarting(true);
    setMessages([]);
    setInputMessage('');
    setSessionId(null);
    
    try {
      const response = await fetch('/api/v1/journaling/session', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ trade: selectedTrade, mode: 'guided'}),
      });
      if (!response.ok) {
        setMessages([{ role: 'assistant', content: 'Sorry, could not start the journaling session.' }]);
        return;
      }
      const data = await response.json();

      setSessionId(data.sessionId);
      
      if (data.initialConversation && Array.isArray(data.initialConversation)) {
        setMessages(data.initialConversation);
      } else if (data.aiMessage) {
        const msgs = Array.isArray(data.aiMessage) ? data.aiMessage : [data.aiMessage];
        setMessages(msgs);
      } else {
        setMessages([]);
      }
    } catch (error) {
      setMessages([{ role: 'assistant', content: 'Sorry, could not start the journaling session.' }]);
    } finally {
      setIsStarting(false);
    }
  };

  // Event handlers
  const handleTradeClick = (trade) => {
    setSelectedTrade(trade);
    setFreeJournalText('');
    setAi_summary(null);
    setIsModalOpen(true);
    setTradeScreenshots([]);
    if (trade?.id) {
      getTradeScreenshots(trade.id).then(setTradeScreenshots).catch(() => {});
    }
    // Load playbooks for picker (only for new journals)
    if (trade.journal_status === 'need_journal') {
      getPlaybooks().then(res => {
        const pbs = res.playbooks || [];
        setPlaybooks(pbs);
        setSelectedPlaybookId(pbs.length === 1 ? pbs[0].id : null);
      }).catch(() => {});
    }

    if (trade.journal_status === 'in_progress') {
      createSessionForInProgress(trade);
      setMessages(trade.conversation || []);
      setJournalMode('freethought-feedback');
    } else if (trade.journal_status === 'done') {
      setMessages(trade.conversation || []);
      setJournalMode('read-only');
    } else {
      setMessages([]);
      setJournalMode(null);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsReplayOpen(false);
    setSelectedTrade(null);
    setMessages([]);
    setJournalMode(null);
    setSessionId(null);
    setAi_summary(null);
    setTradeScreenshots([]);
    setSelectedPlaybookId(null);
    setIsEditingSummary(false);
    setEditedSummary('');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !sessionId) return;

    const userMsg = { role: 'user', content: inputMessage };
    const currentInput = inputMessage;
    setInputMessage('');

    // Show user message immediately
    const messagesWithUser = [...messages, userMsg];
    trackJournalMessageSent({ message_index: messagesWithUser.length });
    setMessages(messagesWithUser);
    setIsLoading(true);

    try {
      const response = await fetch('/api/v1/journaling/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sessionId, message: currentInput })
      });
      if (!response.ok) {
        const errorMsg = { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' };
        const updatedMessages = [...messagesWithUser, errorMsg];
        setMessages(updatedMessages);
        debouncedAutosave(updatedMessages);
        return;
      }
      const data = await response.json();

      const aiMsg = {
        role: 'assistant',
        content: data?.aiMessage || 'Sorry, I did not get a response from the AI.'
      };

      const updatedMessages = [...messagesWithUser, aiMsg];
      setMessages(updatedMessages);
      debouncedAutosave(updatedMessages);

    } catch (error) {
      const errorMsg = { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' };
      const updatedMessages = [...messagesWithUser, errorMsg];
      setMessages(updatedMessages);
      debouncedAutosave(updatedMessages);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAndSummary = async () => {
    setIsSummarizing(true);
    try {
      const response = await fetch('/api/v1/journaling/summary', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          conversation: messages,
          tradeId: selectedTrade.id,
          playbookId: selectedPlaybookId || undefined
        })
      });
      const data = await response.json();
      
      setAi_summary(data);
      trackJournalSessionCompleted({ message_count: messages.length });
      trackJournalSummaryViewed();

      if (data.summaryPassed === false) {
        const aiMsg = { 
          role: 'assistant', 
          content: data.ai_summary || 'AI could not generate a summary. Please add more details to your journal.' 
        };
        const updatedMessages = [...messages, aiMsg];
        setMessages(updatedMessages);
        debouncedAutosave(updatedMessages);
      }
    } catch (error) {
      console.error('Error getting summary:', error);
    } finally {
      setIsSummarizing(false);
    } 
  };

  const handleMoreDetails = async () => {
    if (!sessionId) return;
    
    setIsRequestingDetails(true);
    try {
      const response = await fetch('/api/v1/journaling/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          sessionId, 
          message: "Please provide more detailed analysis and insights about this trade." 
        })
      });
      
      setAi_summary(null);
    } catch (error) {
      console.error('Error getting more details:', error);
    } finally {
      setIsRequestingDetails(false);
    }
  };

  const handleAddMore = () => {
    setAi_summary(null);
  };

  const handleSaveSummaryEdit = async () => {
    if (!selectedTrade?.id) return;
    setIsSavingSummary(true);
    try {
      const res = await fetch('/api/v1/journaling/summary', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ tradeId: selectedTrade.id, ai_summary: editedSummary })
      });
      if (res.ok) {
        setSelectedTrade(prev => ({ ...prev, ai_summary: editedSummary }));
        setTrades(prev => prev.map(t => t.id === selectedTrade.id ? { ...t, ai_summary: editedSummary } : t));
        setIsEditingSummary(false);
      }
    } catch (err) {
      console.error('Error saving summary edit:', err);
    } finally {
      setIsSavingSummary(false);
    }
  };

  const handleEndSession = async () => {
    if (!sessionId) return;
    setIsEndingSession(true);
    
    try {
      let summary_data = null;
      if (ai_summary?.scores) {
        summary_data = {
          emotionalstate: ai_summary.scores.emotionalState,
          risk_management: ai_summary.scores.riskManagement,
          strategyadherence: ai_summary.scores.strategyAdherence
        };
      }
      
      const response = await fetch('/api/v1/journaling/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tradeId: selectedTrade?.id || selectedTrade?._id,
          ai_summary: ai_summary?.ai_summary,
          summary_data,
          cortex_tags: ai_summary?.cortex_tags || null,
          conversation: messages,
          playbookId: selectedPlaybookId || undefined
        })
      });
      
      if (response.ok) {
        setAi_summary(null);
        setSessionId(null);

        // Reload trades
        const tradesData = await fetchTradesFromDB();
        setTrades(tradesData || []);

        // Close the modal
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error ending session:', error);
    } finally {
      setIsEndingSession(false);
    }
  };

  const handleStartJournalingPrompt = () => {
    setJournalMode('choose');
  };

  const handleGuidedJournaling = async () => {
    trackJournalSessionStarted({ mode: 'guided' });
    setJournalMode('guided');
    await startGuidedJournalingSession('guided');
  };

  const handleFreeJournalingClick = () => {
    setJournalMode('freethought');
  };

  const handleFreeJournalingSubmit = async () => {
    if (!freeJournalText.trim()) return;
    
    setIsSummarizing(true);
    const initialMsg = { role: 'user', content: freeJournalText };
    setMessages([initialMsg]);
    setJournalMode('freethought-feedback');
    
    try {
      if (!sessionId) {
        const response = await fetch('/api/v1/journaling/session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            trade: selectedTrade,
            mode: 'free',
            userText: freeJournalText
          })
        });
        if (!response.ok) {
          const errorMsg = { role: 'assistant', content: 'Sorry, could not process your journal entry. Please try again.' };
          const newMessages = [initialMsg, errorMsg];
          setMessages(newMessages);
          return;
        }
        const data = await response.json();
        setSessionId(data.sessionId);
        
        if (data.initialConversation && Array.isArray(data.initialConversation)) {
          setMessages(data.initialConversation);
        } else if (data.aiMessage) {
          const msgs = Array.isArray(data.aiMessage) ? data.aiMessage : [data.aiMessage];
          setMessages(msgs);
        }
      }
    } catch (error) {
      const errorMsg = { role: 'assistant', content: 'Sorry, could not process your journal entry.' };
      const newMessages = [initialMsg, errorMsg];
      setMessages(newMessages);
      debouncedAutosave(newMessages);
    } finally {
      setIsSummarizing(false);
      setFreeJournalText('');
    }
  };


  return (
    <div className="pt-16 h-screen bg-background text-foreground flex flex-col">
      {/* Tabs */}
      <div className="flex border-b border-border bg-card px-2">
        {[
          { key: 'need_journal', label: 'To Journal', count: (groupedTrades.need_journal || []).length },
          { key: 'in_progress', label: 'In Progress', count: (groupedTrades.in_progress || []).length },
          { key: 'done', label: 'Completed', count: (groupedTrades.done || []).length },
        ].map(({ key, label, count }) => (
          <button
            key={key}
            className={`relative px-5 py-3.5 text-sm font-medium transition-all duration-150 flex items-center gap-2 ${
              activeTab === key
                ? 'text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab(key)}
          >
            {label}
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold transition-all duration-150 ${
              activeTab === key
                ? 'bg-crimson/20 text-crimson'
                : 'bg-secondary text-muted-foreground'
            }`}>{count}</span>
            {activeTab === key && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-crimson rounded-t-full" />
            )}
          </button>
        ))}
      </div>

      {/* Trade Cards Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(groupedTrades[activeTab] || []).map((trade, index) => (
            <div
              key={trade._id || trade.id || index}
              className="cursor-pointer"
              onClick={() => handleTradeClick(trade)}
            >
              <JournalTradeList
                trade={trade}
                selectedTrade={null}
                updateSelectedTrade={() => {}}
                darkMode={true}
              />
            </div>
          ))}
        </div>
        {(groupedTrades[activeTab] || []).length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="p-4 rounded-full bg-secondary/60 ring-1 ring-border">
              <MessageSquare className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm">
              {activeTab === 'need_journal' && 'No trades to journal'}
              {activeTab === 'in_progress' && 'No trades in progress'}
              {activeTab === 'done' && 'No completed journals'}
            </p>
          </div>
        )}
      </div>

      {/* Journaling Modal */}
      {isModalOpen && selectedTrade && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 overflow-hidden px-2 sm:px-4" style={{ paddingTop: '5vh', paddingBottom: '5vh' }}>
          <div className="bg-card rounded-2xl shadow-2xl shadow-black/40 w-full max-w-6xl h-[90vh] max-h-[90vh] min-h-0 flex flex-col border border-border overflow-hidden" style={{ height: '90vh' }}>
            {/* Modal Header */}
            <div className="flex-shrink-0 p-4 pr-12 border-b border-border flex flex-wrap justify-between items-center gap-2 bg-card z-10 relative">
              <div className="min-w-0">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    isBuyDirection(selectedTrade.direction) ? 'bg-green-700/80 text-green-200' : 'bg-red-700/80 text-red-200'
                  }`}>
                    {selectedTrade.direction}
                  </span>
                  {selectedTrade.symbol}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {new Date(selectedTrade.trade_date).toLocaleDateString()}
                </p>
                {tradeScreenshots.length > 0 && (
                  <ScreenshotThumbnail
                    screenshots={tradeScreenshots}
                    maxVisible={3}
                    className="mt-2"
                  />
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setIsReplayOpen(true)}
                  className="bg-secondary text-secondary-foreground px-3.5 py-2 rounded-lg hover:bg-secondary/80 border border-border flex items-center gap-2 text-sm transition-all duration-150"
                >
                  <TrendingUp className="w-4 h-4" />
                  View Replay
                </button>
                {(journalMode === 'guided' || journalMode === 'freethought-feedback') && (
                  <button
                    className="bg-crimson text-white px-3.5 py-2 rounded-lg hover:bg-crimson/90 disabled:opacity-50 flex items-center gap-2 text-sm transition-all duration-150"
                    onClick={handleSaveAndSummary}
                    disabled={isSummarizing}
                  >
                    <Brain className="w-4 h-4" />
                    {isSummarizing ? 'Generating...' : 'Generate Summary'}
                  </button>
                )}
              </div>
              {/* Close button — always accessible, pinned top-right */}
              <button
                onClick={handleCloseModal}
                className="absolute top-3 right-3 text-muted-foreground hover:text-foreground p-1.5 rounded-lg hover:bg-secondary transition-all duration-150"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              {/* No mode selected - show options */}
              {!journalMode && (
                <div className="flex flex-col items-center justify-center flex-1 space-y-6 p-6">
                  {/* Playbook picker */}
                  {playbooks.length > 1 && (
                    <div className="w-full max-w-sm">
                      <label className="block text-sm font-medium text-foreground mb-1.5">Playbook</label>
                      <select
                        value={selectedPlaybookId || ''}
                        onChange={(e) => setSelectedPlaybookId(e.target.value || null)}
                        className="w-full px-3 py-2 rounded-md border border-border bg-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="">No playbook (standard rules)</option>
                        {playbooks.map((pb) => (
                          <option key={pb.id} value={pb.id}>{pb.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground mt-1">Rules from this playbook will be used for analysis</p>
                    </div>
                  )}
                  <div className="text-center mb-2">
                    <p className="text-base text-muted-foreground">How would you like to journal this trade?</p>
                  </div>
                  <div className="flex gap-4">
                    <button
                      onClick={handleFreeJournalingClick}
                      className="flex flex-col items-center gap-2 px-8 py-5 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80 border border-border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 min-w-[160px]"
                    >
                      <FileText className="w-6 h-6 text-muted-foreground" />
                      <span className="font-semibold text-foreground">Freeform Start</span>
                      <span className="text-xs text-muted-foreground">Write everything freely. AI will analyze your entry and then guide you with follow-up questions to complete your journal.</span>
                    </button>
                    <button
                      onClick={handleGuidedJournaling}
                      disabled={isStarting}
                      className="flex flex-col items-center gap-2 px-8 py-5 bg-crimson/10 text-foreground rounded-xl hover:bg-crimson/20 border border-crimson/30 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 min-w-[160px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      <Brain className="w-6 h-6 text-crimson" />
                      <span className="font-semibold text-foreground">{isStarting ? 'Starting...' : 'Guided by AI'}</span>
                      <span className="text-xs text-muted-foreground">Let AI lead. It will ask the right questions to help you build a complete, structured journal.</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Free journaling textarea */}
              {journalMode === 'freethought' && (
                <div className="flex flex-col flex-1 p-6 gap-4">
                  <textarea
                    className="flex-1 w-full border border-border bg-input text-foreground rounded-xl p-4 text-base placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-border"
                    placeholder="Write your thoughts about this trade..."
                    value={freeJournalText}
                    onChange={e => setFreeJournalText(e.target.value)}
                    disabled={isSummarizing}
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={handleFreeJournalingSubmit}
                      disabled={isSummarizing || !freeJournalText.trim()}
                      className="px-6 py-2.5 bg-crimson text-white rounded-full text-sm font-semibold hover:bg-crimson/90 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                    >
                      {isSummarizing ? 'Analyzing...' : 'Submit Journal'}
                    </button>
                  </div>
                </div>
              )}

              {/* Chat interface */}
              {(journalMode === 'guided' || journalMode === 'freethought-feedback' || journalMode === 'read-only') && (
                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  <div ref={chatContainerRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 space-y-4">
                    {visibleMessages.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                          msg.role === 'user'
                            ? 'bg-crimson text-white shadow-sm rounded-br-sm'
                            : 'bg-secondary text-secondary-foreground border border-border rounded-bl-sm'
                        }`}>
                          {parse(msg.content)}
                        </div>
                      </div>
                    ))}

                    {/* Typing indicator while waiting for AI response */}
                    {isLoading && (
                      <div className="flex justify-start">
                        <div className="rounded-2xl rounded-bl-sm px-4 py-3 bg-secondary border border-border flex items-center gap-1.5">
                          <span className="typing-dot" />
                          <span className="typing-dot" style={{ animationDelay: '0.2s' }} />
                          <span className="typing-dot" style={{ animationDelay: '0.4s' }} />
                        </div>
                      </div>
                    )}

                    {/* Show AI Summary in read-only mode - inside scrollable area */}
                    {journalMode === 'read-only' && (
                      <div className="mt-6 pt-6 border-t border-border">
                        <div className="flex items-center justify-between mb-3">
                          <div className="font-semibold text-card-foreground text-lg">AI Summary</div>
                          {!isEditingSummary ? (
                            <button
                              onClick={() => { setEditedSummary(selectedTrade.ai_summary || ''); setIsEditingSummary(true); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-secondary border border-border rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-all duration-150"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                              Edit
                            </button>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setIsEditingSummary(false)}
                                className="px-3 py-1.5 text-xs font-medium bg-secondary border border-border rounded-lg text-muted-foreground hover:text-foreground transition-all duration-150"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleSaveSummaryEdit}
                                disabled={isSavingSummary}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-crimson text-white rounded-lg hover:bg-crimson/90 disabled:opacity-50 transition-all duration-150"
                              >
                                <Save className="w-3.5 h-3.5" />
                                {isSavingSummary ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          )}
                        </div>
                        {isEditingSummary ? (
                          <textarea
                            className="w-full bg-input border border-border rounded-lg p-4 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-border leading-relaxed"
                            rows={10}
                            value={editedSummary}
                            onChange={e => setEditedSummary(e.target.value)}
                          />
                        ) : selectedTrade.ai_summary ? (
                          (() => {
                            const sd = selectedTrade.summary_data || {};
                            const scores = {
                              emotionalState: sd.emotionManagement ?? sd.emotionalstate,
                              riskManagement: sd.riskManagement ?? sd.risk_management,
                              strategyAdherence: sd.strategyManagement ?? sd.strategyadherence,
                            };
                            const hasScores = scores.emotionalState != null || scores.riskManagement != null || scores.strategyAdherence != null;
                            const cortex_tags = selectedTrade.cortex_tags;
                            const tagPills = buildTagPills(cortex_tags);
                            const ruleBreaks = cortex_tags?.rule_breaking_phrases?.filter(r => r?.phrase) || [];
                            const cleanText = stripRedundantSections(selectedTrade.ai_summary);
                            const sections = parseSections(cleanText);
                            return (
                              <div className="space-y-4">
                                {sections.length > 0 ? (
                                  <div className="space-y-3">
                                    {sections.map((s, i) => <SectionBlock key={i} heading={s.heading} content={s.content} />)}
                                  </div>
                                ) : (
                                  <div className="p-4 bg-secondary rounded-lg border border-border">
                                    <p className="text-sm text-foreground whitespace-pre-line">{cleanText}</p>
                                  </div>
                                )}
                                {hasScores && (
                                  <div className="p-4 bg-secondary rounded-lg border border-border">
                                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Performance Scores</h3>
                                    <div className="flex flex-wrap gap-x-6 gap-y-3">
                                      {scores.emotionalState != null && <ScoreBar label="Emotional Control" value={scores.emotionalState} />}
                                      {scores.riskManagement != null && <ScoreBar label="Risk Management" value={scores.riskManagement} />}
                                      {scores.strategyAdherence != null && <ScoreBar label="Strategy Adherence" value={scores.strategyAdherence} />}
                                    </div>
                                  </div>
                                )}
                                {tagPills.length > 0 && (
                                  <div>
                                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Behavioral Insights</h3>
                                    <div className="flex flex-wrap gap-2">
                                      {tagPills.map((t, i) => <TagPill key={i} label={t.label} color={t.color} />)}
                                    </div>
                                  </div>
                                )}
                                {ruleBreaks.length > 0 && (
                                  <div className="p-3 bg-red-900/10 border border-red-900/30 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">Rule Violations</span>
                                    </div>
                                    <div className="space-y-2">
                                      {ruleBreaks.map((r, i) => (
                                        <div key={i} className="text-xs text-muted-foreground">
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full border mr-2 font-medium ${
                                            r.severity === 'high' ? 'bg-red-900/40 border-red-600/40 text-red-300'
                                            : r.severity === 'medium' ? 'bg-orange-900/40 border-orange-600/40 text-orange-300'
                                            : 'bg-yellow-900/40 border-yellow-600/40 text-yellow-300'
                                          }`}>
                                            {r.tag?.replace(/_/g, ' ')}
                                          </span>
                                          <span className="italic">"{r.phrase}"</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          <div className="bg-secondary border border-border rounded-lg p-6 text-muted-foreground text-sm">
                            No summary available.
                          </div>
                        )}
                      </div>
                    )}

                    <div ref={chatEndRef} />
                  </div>

                  {/* Input area - hide in read-only mode */}
                  {journalMode !== 'read-only' && (
                    <form onSubmit={handleSendMessage} className="flex-shrink-0 p-4 border-t border-border bg-card z-10">
                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={inputMessage}
                          onChange={(e) => setInputMessage(e.target.value)}
                          placeholder="Type your message..."
                          className="flex-1 border border-border rounded-full px-5 py-2.5 bg-input text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-1 focus:ring-border"
                          disabled={isLoading}
                        />
                        <button
                          type="submit"
                          className="bg-crimson text-white p-2.5 rounded-full hover:bg-crimson/90 disabled:opacity-50 transition-all duration-150 flex-shrink-0"
                          disabled={!inputMessage.trim() || isLoading}
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isReplayOpen && selectedTrade && (
        <TradeReplayModal
          trade={selectedTrade}
          onClose={() => setIsReplayOpen(false)}
        />
      )}

      {/* Summary Modal */}
      <SummaryModal
        ai_summary={ai_summary}
        onMoreDetails={handleMoreDetails}
        onAddMore={handleAddMore}
        onEndSession={handleEndSession}
        isRequestingDetails={isRequestingDetails}
        isEndingSession={isEndingSession}
      />
    </div>
  );
};

export default JournalAI;